import React from "react"
import { Helmet } from "react-helmet-async"

interface SEOHeadProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: "website" | "article" | "product"
  structuredData?: object
  canonicalUrl?: string
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Snap Fire Systems - Leading Fire Protection Solutions",
  description = "Snap Fire Systems provides innovative fire protection solutions including fire collars, fire stopping systems, and comprehensive fire management plans for commercial and residential buildings.",
  keywords = "fire protection, fire collars, fire stopping, fire safety, building safety, fire management, fire systems, fire prevention",
  image = "/assets/images/HomePageBannerImage.png",
  url = "https://snapfiresystems.com",
  type = "website",
  structuredData,
  canonicalUrl
}) => {
  const fullUrl = canonicalUrl ?? url

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Snap Fire Systems" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Snap Fire Systems" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@snapfiresystems" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#1a365d" />
      <meta name="msapplication-TileColor" content="#1a365d" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Structured Data */}
      {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}

      {/* Default Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Snap Fire Systems",
          url: "https://snapfiresystems.com",
          logo: "https://snapfiresystems.com/assets/images/logo.png",
          description: "Leading provider of fire protection solutions and fire management systems",
          address: {
            "@type": "PostalAddress",
            addressCountry: "AU"
          },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            availableLanguage: "English"
          },
          sameAs: ["https://www.linkedin.com/company/snap-fire-systems", "https://www.facebook.com/snapfiresystems"]
        })}
      </script>
    </Helmet>
  )
}

// Predefined SEO configurations for different page types
export const SEOConfigs = {
  home: {
    title: "Snap Fire Systems - Leading Fire Protection Solutions",
    description:
      "Discover innovative fire protection solutions including fire collars, fire stopping systems, and comprehensive fire management plans. Protect your building with Snap Fire Systems.",
    keywords: "fire protection, fire collars, fire stopping, fire safety, building safety, fire management",
    type: "website" as const
  },
  products: {
    title: "Fire Protection Products - Snap Fire Systems",
    description:
      "Explore our comprehensive range of fire protection products including cast-in fire collars, retrofit fire collars, gas fire collars, and more.",
    keywords: "fire collars, cast-in fire collars, retrofit fire collars, gas fire collars, fire protection products",
    type: "website" as const
  },
  contact: {
    title: "Contact Snap Fire Systems - Get Expert Fire Protection Advice",
    description: "Contact Snap Fire Systems for expert fire protection advice, product information, and professional fire safety solutions.",
    keywords: "contact fire protection, fire safety consultation, fire protection advice",
    type: "website" as const
  },
  fmp: {
    title: "Fire Management Plans - Snap Fire Systems",
    description: "Create and manage comprehensive fire management plans with Snap Fire Systems. Professional fire safety planning and documentation.",
    keywords: "fire management plans, fire safety planning, fire documentation, FMP",
    type: "website" as const
  }
}
